import React, { useMemo } from 'react';
import { Box, Card, CardContent, Typography, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import RegulatorySheet from './RegulatorySheet';

export type Section = { label: string; prefixes: string[]; side: 'debit' | 'credit' };
export type EtatsTemplate = { bilan: Section[] };

export type BalanceRow = { numero_compte: string; debit: number; credit: number; solde: number };

const fmt = (n: number) => new Intl.NumberFormat('fr-FR', { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

interface Props {
  template: EtatsTemplate;
  balanceRows: BalanceRow[];
  title?: string;
  periode?: { from?: string; to?: string };
  entityName?: string;
  systemLabel?: string;
  currency?: string;
}

const BilanReglementaire: React.FC<Props> = ({ template, balanceRows, title = 'Bilan réglementaire', periode, entityName = 'Entité EBNL', systemLabel = 'SN', currency = 'XOF' }) => {
  const { actifs, passifs, totalActif, totalPassif, maxLen } = useMemo(() => {
    const sumSection = (s: Section): number => {
      const match = balanceRows.filter(r => s.prefixes.some(p => r.numero_compte.startsWith(p)));
      const netDebit = (r: BalanceRow) => Math.max(r.debit - r.credit, 0);
      const netCredit = (r: BalanceRow) => Math.max(r.credit - r.debit, 0);
      const sectionTotal = match.reduce((acc, r) => acc + (s.side === 'debit' ? netDebit(r) : netCredit(r)), 0);
      return sectionTotal;
    };

    const actifSections = template.bilan.filter(s => s.side === 'debit');
    const passifSections = template.bilan.filter(s => s.side === 'credit');

    const actifs = actifSections.map(s => ({ label: s.label, amount: sumSection(s) }));
    const passifs = passifSections.map(s => ({ label: s.label, amount: sumSection(s) }));

    const totalActif = actifs.reduce((a, r) => a + r.amount, 0);
    const totalPassif = passifs.reduce((a, r) => a + r.amount, 0);

    const maxLen = Math.max(actifs.length, passifs.length);
    return { actifs, passifs, totalActif, totalPassif, maxLen };
  }, [template, balanceRows]);

  return (
    <RegulatorySheet
      title={title}
      entity={entityName}
      systemLabel={systemLabel}
      currency={currency}
      periodText={`Exercice ${periode?.from || '…'} au ${periode?.to || '…'}`}
    >
      <Table size="small" sx={{ mt: 0,
        '& th, & td': { borderColor: '#000', borderWidth: '1px', borderStyle: 'solid', p: 0.5 },
        '& thead th': { backgroundColor: '#f9f9f9' }
      }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: '35%', fontWeight: 700 }}>Actif</TableCell>
            <TableCell align="right" sx={{ width: '15%', fontWeight: 700 }}>Montant</TableCell>
            <TableCell sx={{ width: '35%', fontWeight: 700 }}>Passif</TableCell>
            <TableCell align="right" sx={{ width: '15%', fontWeight: 700 }}>Montant</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Array.from({ length: maxLen }).map((_, i) => (
            <TableRow key={i}>
              <TableCell>{actifs[i]?.label || ''}</TableCell>
              <TableCell align="right">{actifs[i] ? fmt(actifs[i].amount) : ''}</TableCell>
              <TableCell>{passifs[i]?.label || ''}</TableCell>
              <TableCell align="right">{passifs[i] ? fmt(passifs[i].amount) : ''}</TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Total Actif</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(totalActif)}</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Total Passif</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(totalPassif)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </RegulatorySheet>
  );
};

export default BilanReglementaire;